import { FilterOption } from "../modals/MultiSelectFilterModal";
import { SubprocessOption } from "../select/SubprocessCells";

// Subprocess helpers shared by the OS screen and SubprocessModalSelect: turning the
// Subprocess codebook into picker options, and rendering a set of ids as display text.

const resolvePd = (
  pd: SubprocessOption["processDefinition"]
): { id: string; name: string; order: number } => {
  if (!pd) return { id: "__none__", name: "", order: 0 };
  if (typeof pd === "string") return { id: pd, name: "", order: 0 };
  return { id: pd._id || "__none__", name: pd.name || pd.code || "", order: (pd as any).order ?? 0 };
};

// Groups subprocesses by their parent process definition (same order the UI
// shows them in), and flattens to selectable options. Shared with the header
// Excel export so the exported Подпроцеси text matches the checkbox display 1:1.
export const buildSubprocessOptions = (subprocesses: SubprocessOption[]): FilterOption[] => {
  const map = new Map<string, { name: string; order: number; stages: SubprocessOption[] }>();
  subprocesses.forEach((sp) => {
    const { id, name, order } = resolvePd(sp.processDefinition);
    const g = map.get(id);
    if (g) g.stages.push(sp);
    else map.set(id, { name, order, stages: [sp] });
  });
  const arr = Array.from(map.values());
  const byOrderName = (a: { order?: number; name?: string }, b: { order?: number; name?: string }) =>
    (a.order ?? 0) - (b.order ?? 0) || (a.name || "").localeCompare(b.name || "", "mk");
  arr.sort(byOrderName);
  arr.forEach((g) => g.stages.sort(byOrderName as any));
  return arr.flatMap((g) =>
    g.stages.map((sp): FilterOption => ({
      _id: sp._id,
      code: g.name || "",
      name: sp.name || "",
    }))
  );
};

/** Selected subprocess names, in the same order the checkbox UI shows them, joined with ", ". */
export const resolveSubprocessNamesText = (selectedIds: string[], subprocesses: SubprocessOption[]): string => {
  const selected = new Set(selectedIds);
  return buildSubprocessOptions(subprocesses)
    .filter((option) => selected.has(option._id))
    .map((option) => option.name)
    .join(", ");
};

// NOTE: this file used to also export a default `OSSubprocessCheckboxes` picker component.
// It was never rendered anywhere — per-ident subprocess selection is derived, not picked
// (see OS.tsx `selectedSubprocessIds`) — so it was removed. The two helpers above are the
// live surface: `buildSubprocessOptions` (SubprocessModalSelect) and
// `resolveSubprocessNamesText` (OS.tsx header Excel export).
